<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">

    <!-- Page Header -->
    <div class="mb-8 flex items-center gap-4">
      <div class="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl
                  flex items-center justify-center shadow-lg shadow-orange-500/30">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
        </svg>
      </div>
      <div>
        <h1 class="text-2xl font-bold text-slate-900">{{ review.id ? "Update Review" : "Create New Review" }}</h1>
        <p class="text-sm text-slate-500 mt-0.5">Manage customer review details</p>
      </div>
    </div>

    <!-- Form Card -->
    <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

      <!-- Card Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-700 border-b border-orange-600">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
          </svg>
          <span class="text-white font-semibold text-sm">Review Information</span>
        </div>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loading" class="flex items-center justify-center py-24">
        <div class="flex flex-col items-center gap-3">
          <div class="w-10 h-10 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
          <p class="text-sm text-slate-400">Loading…</p>
        </div>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="onSubmit" class="p-6 space-y-6">

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Full Name -->
          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-slate-700 mb-2">
              Customer Name
            </label>
            <input
              v-model="review.full_name"
              type="text"
              placeholder="e.g. John Doe"
              class="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-700
                     focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 outline-none transition-all duration-200
                     placeholder:text-slate-300"
              :class="{ 'border-red-400 ring-2 ring-red-400/20': errors.full_name?.[0] }"
            />
            <p v-if="errors.full_name?.[0]" class="mt-1.5 text-xs text-red-500 font-medium">{{ errors.full_name[0] }}</p>
          </div>

          <!-- Review Title -->
          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-slate-700 mb-2">
              Review Title
            </label>
            <input
              v-model="review.title"
              type="text"
              placeholder="e.g. Amazing service!"
              class="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-700
                     focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 outline-none transition-all duration-200
                     placeholder:text-slate-300"
              :class="{ 'border-red-400 ring-2 ring-red-400/20': errors.title?.[0] }"
            />
            <p v-if="errors.title?.[0]" class="mt-1.5 text-xs text-red-500 font-medium">{{ errors.title[0] }}</p>
          </div>

          <!-- Description -->
          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-slate-700 mb-2">
              Review Content
            </label>
            <textarea
              v-model="review.description"
              placeholder="Write the full review text here..."
              rows="5"
              class="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-700
                     focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 outline-none transition-all duration-200
                     placeholder:text-slate-300 resize-y"
              :class="{ 'border-red-400 ring-2 ring-red-400/20': errors.description?.[0] }"
            ></textarea>
            <p v-if="errors.description?.[0]" class="mt-1.5 text-xs text-red-500 font-medium">{{ errors.description[0] }}</p>
          </div>

          <!-- Rating -->
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">
              Rating
            </label>
            <div class="p-3 border border-slate-200 rounded-xl bg-slate-50 inline-block">
              <Rating
                v-model="review.rate"
                :cancel="false"
                :stars="5"
                class="text-orange-500 gap-1"
              />
            </div>
            <p v-if="errors.rate?.[0]" class="mt-1.5 text-xs text-red-500 font-medium">{{ errors.rate[0] }}</p>
          </div>

          <!-- Active Toggle -->
          <div class="flex flex-col justify-end">
            <label class="flex items-center gap-3 p-3 border border-slate-200 rounded-xl bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors w-fit">
              <div class="relative inline-flex items-center">
                <input type="checkbox" v-model="review.active" class="sr-only peer" />
                <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300
                            rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white
                            after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white
                            after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all
                            peer-checked:bg-orange-500"></div>
              </div>
              <span class="text-sm font-semibold text-slate-700">Display this review actively?</span>
            </label>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-3 pt-6 mt-2 border-t border-slate-100">
          <router-link
            :to="{ name: 'app.reviews' }"
            class="px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl
                   hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
          >
            Cancel
          </router-link>
          <button
            type="submit"
            :disabled="loading"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600
                   hover:from-orange-600 hover:to-orange-700 text-white text-sm font-semibold rounded-xl
                   shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5
                   disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          >
            <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            {{ review.id ? 'Update Review' : 'Save Review' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";
import Rating from "primevue/rating";
import { useToast } from "primevue/usetoast";

const toast = useToast();
const route = useRoute();
const router = useRouter();

const loading = ref(false);
const review = ref({
  id: null,
  full_name: "",
  rate: null,
  active: false,
  title: "",
  description: "",
});

const errors = ref({
  full_name: [],
  rate: [],
  active: [],
  title: [],
  description: [],
});

const showSuccess = (detail) => {
  toast.add({
    severity: "success",
    summary: "Success",
    detail,
    life: 3000,
  });
};

function onSubmit() {
  loading.value = true;
  errors.value = {}; // Reset errors before submission
  const action = review.value.id ? "updateReview" : "createReview";

  store
    .dispatch(action, review.value)
    .then(() => {
      showSuccess(
        review.value.id
          ? "Review has been successfully updated."
          : "Review has been successfully created."
      );
      router.push({ name: "app.reviews" });
    })
    .catch((err) => {
      if (err.response?.status === 422) {
        errors.value = err.response.data.errors;
      } else if (err.response?.status === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: err.response.data.message,
          life: 3000,
        });
      }
    })
    .finally(() => {
      loading.value = false;
    });
}

onMounted(() => {
  if (route.params.id) {
    loading.value = true;
    store
      .dispatch("getReview", route.params.id)
      .then((res) => {
        review.value = res.data;
      })
      .catch((err) => console.error("Failed to load review:", err))
      .finally(() => {
        loading.value = false;
      });
  }
});
</script>

<style scoped>
/* Customize the PrimeVue Rating aspect if needed */
:deep(.p-rating-item.p-rating-item-active .p-rating-icon) {
  color: #f97316; /* Tailwind orange-500 */
}
:deep(.p-rating-item:not(.p-rating-item-active) .p-rating-icon) {
  color: #cbd5e1; /* Tailwind slate-300 */
}
:deep(.p-rating:not(.p-disabled):not(.p-readonly) .p-rating-item:hover .p-rating-icon) {
  color: #ea580c; /* Tailwind orange-600 */
}
</style>
